import type { GameSprite } from "../types"
import type { BaseEnemyRuntime } from "./enemy-system"

interface CollisionRulesOptions {
  getNow: () => number
  getPlayerPowerMode: () => "overdrive" | null
  isPlayerInvulnerable: () => boolean
  isBossEnemy: (enemy: GameSprite) => boolean
  defeatEnemy: (enemy: GameSprite) => void
  damageBossEnemy: (enemy: GameSprite, ratio: number, color: number) => void
  handlePlayerDamage: (
    damage: number,
    source: "enemy" | "projectile" | "floor" | "generic",
  ) => void
  createImpactBurst: (
    x: number,
    y: number,
    color: number,
    scale: number,
  ) => void
  collectCoin: (value: number, x: number, y: number) => void
  onCoinPickup: (coin: GameSprite) => void
  projectileInfoColor: number
  projectileDangerColor: number
}

const OVERDRIVE_BOSS_DAMAGE = 0.06 as const
const KAMIKAZE_EXPLOSION_DAMAGE_MULTIPLIER = 2
const KAMIKAZE_EXPLOSION_BURST_SCALE = 2.5
const KAMIKAZE_EXPLOSION_COLOR = 0xff4444

export class CollisionRules {
  private readonly options: CollisionRulesOptions

  public constructor(options: CollisionRulesOptions) {
    this.options = options
  }

  public onDogEnemyCollision(enemy: GameSprite): void {
    const enemyData = enemy.entityData
    const powerMode = this.options.getPlayerPowerMode()
    const enemyRuntime = enemy.getData("enemyRuntime") as
      | BaseEnemyRuntime
      | undefined
    const heroCollisionResponse = enemyRuntime?.onHeroCollision()

    if (powerMode === "overdrive") {
      if (this.options.isBossEnemy(enemy)) {
        this.options.damageBossEnemy(enemy, OVERDRIVE_BOSS_DAMAGE, 0xff00ff)
      } else {
        this.options.defeatEnemy(enemy)
      }
      return
    }

    if (!this.options.isPlayerInvulnerable() && enemyData?.kind === "enemy") {
      if (heroCollisionResponse === "damage-and-defeat") {
        this.options.handlePlayerDamage(enemyData.damage, "enemy")
        this.options.defeatEnemy(enemy)
        return
      }

      if (enemyData.type === "kamikaze" && enemyData.hp > 0) {
        this.options.createImpactBurst(
          enemy.x,
          enemy.y,
          KAMIKAZE_EXPLOSION_COLOR,
          KAMIKAZE_EXPLOSION_BURST_SCALE,
        )
        this.options.handlePlayerDamage(
          enemyData.damage * KAMIKAZE_EXPLOSION_DAMAGE_MULTIPLIER,
          "enemy",
        )
        this.options.defeatEnemy(enemy)
      } else {
        this.options.handlePlayerDamage(enemyData.damage, "enemy")
      }
    }
  }

  public onDogProjectileCollision(projectile: GameSprite): void {
    const projectileData = projectile.entityData
    if (
      !projectileData ||
      projectileData.kind !== "projectile" ||
      !projectileData.isEnemy
    ) {
      return
    }

    const powerMode = this.options.getPlayerPowerMode()
    if (powerMode === "overdrive") {
      this.options.createImpactBurst(
        projectile.x,
        projectile.y,
        this.options.projectileInfoColor,
        1,
      )
      projectile.destroy()
      return
    }

    if (!this.options.isPlayerInvulnerable()) {
      this.options.handlePlayerDamage(projectileData.damage, "projectile")
      this.options.createImpactBurst(
        projectile.x,
        projectile.y,
        this.options.projectileDangerColor,
        1,
      )
      projectile.destroy()
    }
  }

  public onDogCoinCollision(coin: GameSprite): void {
    const coinData = coin.entityData
    if (!coinData || coinData.kind !== "coin") return

    this.options.collectCoin(coinData.value, coin.x, coin.y)
    this.options.onCoinPickup(coin)
    coin.destroy()
  }

  public onBumperProjectileCollision(projectile: GameSprite): void {
    const projectileData = projectile.entityData
    if (
      !projectileData ||
      projectileData.kind !== "projectile" ||
      !projectileData.isEnemy
    ) {
      return
    }

    projectile.destroy()
    this.options.createImpactBurst(
      projectile.x,
      projectile.y,
      this.options.projectileInfoColor,
      0.8,
    )
  }
}
